import { z } from 'zod';

import { IdSchema, IsoDateSchema } from './common.js';

// Body-weight log. Minimal by design — see ADR-0017 and the M6.1 milestone
// notes. weightKg lives as a number on the wire; the Prisma row is Decimal(5,2)
// so anything outside [20, 300] is rejected before it reaches the database.
export const WeightLogSchema = z.object({
  id: IdSchema,
  userId: IdSchema,
  weightKg: z.number().min(20).max(300),
  loggedAt: IsoDateSchema,
  notes: z.string().max(200).nullable(),
  createdAt: IsoDateSchema,
});
export type WeightLog = z.infer<typeof WeightLogSchema>;

// POST /v1/logs/weight body. loggedAt is optional — server defaults to
// `new Date()` at write time so casual entries do not have to plumb a clock
// through the UI. notes is optional and capped at 200 chars to keep the
// surface scoped to "I weighed myself", not journaling.
export const CreateWeightLogRequestSchema = z
  .object({
    weightKg: z
      .number({
        required_error: 'Enter a value between 20 and 300 kg',
        invalid_type_error: 'Enter a value between 20 and 300 kg',
      })
      .min(20, 'Must be at least 20 kg')
      .max(300, 'Must be 300 kg or less'),
    loggedAt: IsoDateSchema.optional(),
    notes: z.string().max(200).optional(),
  })
  .strict();
export type CreateWeightLogRequest = z.infer<typeof CreateWeightLogRequestSchema>;

export const UpdateWeightLogRequestSchema = CreateWeightLogRequestSchema.partial().refine(
  (value) => Object.keys(value).length > 0,
  { message: 'At least one field is required' },
);
export type UpdateWeightLogRequest = z.infer<typeof UpdateWeightLogRequestSchema>;

// GET /v1/logs/weight query (server-side coercion via z.coerce.number, since
// query strings are always strings on the wire). page/pageSize indexing keeps
// the surface simple for M6.1; switch to cursor pagination if a user ever
// scrolls past a few thousand entries (unlikely for self-reported weights).
export const ListWeightLogsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  from: IsoDateSchema.optional(),
  to: IsoDateSchema.optional(),
});
export type ListWeightLogsQuery = z.infer<typeof ListWeightLogsQuerySchema>;

export const WeightLogListResponseSchema = z.object({
  items: z.array(WeightLogSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(100),
});
export type WeightLogListResponse = z.infer<typeof WeightLogListResponseSchema>;
